import type { InsuranceDrpSettings } from '../../../../hooks/useGetSettings';
import type { InsuranceSearchResult } from '../../../../hooks/useSearchInsurance';
import { ruleFieldsOf, withAutomaticPriceMatchEstimate } from '../../../../components/settings/insuranceRuleSets';
import type { PriceMatchEntry } from '../../../../components/settings/priceMatchCriteria';
import type { SubCompanyMode } from '../../../../components/settings/SubCompanyTable';

/** The DRP screen's name for a group company's fate; see SubCompanyMode. */
export type DrpSubMode = SubCompanyMode;

/** One carrier being added or edited, before anything is saved. */
export interface DrpDraft {
    insurerId: string;
    insurerName: string;
    /** Group the carrier belongs to; what its sub companies are looked up by. */
    groupId?: string;
    /**
     * The group's parent, when this carrier is itself a branched sub company.
     * Only a label — it is what makes the row read "Allstate -> SafeAuto".
     */
    parentInsurerName?: string;
    /** What to do with each company in the group, by insurer id. */
    subModes: Record<string, DrpSubMode>;
    /**
     * Set when the user chooses to fold this carrier into a group mate that is
     * already configured: no new rules are written, it just follows that entry.
     */
    joinParentId?: string;
    joinParentName?: string;
    /** Selected location ids. Saved as 'all' when every location is picked. */
    locationIds: Array<string>;
    note: string;
    /** Kept as text so a half-typed value survives (e.g. "1" before "10"). */
    orderingThreshold: string;
    priceMatchEstimate: boolean;
    priceMatchCCC: boolean;
    priceMatchPartstrader: boolean;
    priceMatchCustom: boolean;
    priceMatchCustomRanking: Array<PriceMatchEntry>;
    prohibitedSuppliers: Array<{ _id: string; title: string }>;
    /** Set from the search result: this carrier expects CCC price matching. */
    sourcePartsRequired: boolean;
    /** Set from the search result: this carrier expects PartsTrader matching. */
    partstraderRequired: boolean;
}

export const buildDrpDraft = (insurance?: InsuranceDrpSettings): DrpDraft => {
    const priceMatch = withAutomaticPriceMatchEstimate({
        priceMatchEstimate: insurance?.priceMatchEstimate ?? false,
        priceMatchCCC: insurance?.priceMatchCCC ?? false,
        priceMatchPartstrader: insurance?.priceMatchPartstrader ?? false,
        priceMatchCustom: insurance?.priceMatchCustom ?? false,
    });

    return {
        insurerId: insurance?.insurerId ?? '',
        insurerName: insurance?.insurerName ?? '',
        groupId: undefined,
        parentInsurerName: undefined,
        subModes: {},
        joinParentId: undefined,
        joinParentName: undefined,
        locationIds: [],
        note: insurance?.note ?? '',
        orderingThreshold:
            insurance?.orderingThreshold != null
                ? String(Math.round(insurance.orderingThreshold * 100))
                : '',
        ...priceMatch,
        priceMatchCustomRanking: insurance?.priceMatchCustomRanking ?? [],
        prohibitedSuppliers: insurance?.prohibitedSuppliers ?? [],
        sourcePartsRequired: false,
        partstraderRequired: false,
    };
};

/** Location ids this carrier already covers, for seeding the location list. */
export const savedLocationIds = (
    insurance: InsuranceDrpSettings | undefined,
    allLocationIds: Array<string>,
): Array<string> => {
    if (!insurance) return allLocationIds;
    if (insurance.locations === 'all') return allLocationIds;
    return insurance.locations.map((location) => location._id);
};

/**
 * The mode each group company starts on. Companies already configured keep the
 * shape they were saved in, so opening a carrier shows the truth rather than a
 * proposal. Ones PartBay has never been told about default to riding along when
 * a carrier is first added — a group is normally taken on whole — and to being
 * left alone when an existing carrier is edited, so a later visit to this
 * screen never quietly signs the shop up for more partners.
 *
 * 'separate' is never seeded: branching is something the user does here, and a
 * company that already stands on its own is 'elsewhere' — nothing to write.
 */
export const seedSubModes = (
    relatedCompanies: Array<InsuranceSearchResult>,
    insurance: Array<InsuranceDrpSettings>,
    primaryInsurerId: string,
    isAddMode: boolean,
): Record<string, DrpSubMode> => {
    const byId = new Map(insurance.map((entry) => [entry.insurerId, entry]));
    const modes: Record<string, DrpSubMode> = {};

    for (const company of relatedCompanies) {
        const saved = byId.get(company._id);
        if (!saved) {
            modes[company._id] = isAddMode ? 'same' : 'none';
            continue;
        }
        modes[company._id] = 'elsewhere';
    }

    return modes;
};

/**
 * The settings shape for one carrier. Every location selected saves as 'all',
 * so adding a shop later keeps applying to the carrier, and a custom ranking
 * only counts when it has entries — otherwise the CCC/PartsTrader flags stand.
 */
export const toInsurancePayload = (
    draft: DrpDraft,
    insurerId: string,
    insurerName: string,
    locations: Array<{ _id: string; title: string }>,
): InsuranceDrpSettings => {
    const allSelected =
        locations.length > 0 && locations.every((location) => draft.locationIds.includes(location._id));
    const hasCustomRanking = draft.priceMatchCustom && draft.priceMatchCustomRanking.length > 0;

    return {
        insurerId,
        insurerName,
        locations: allSelected
            ? 'all'
            : locations
                  .filter((location) => draft.locationIds.includes(location._id))
                  .map((location) => ({ _id: location._id, title: location.title })),
        prohibitedSuppliers: draft.prohibitedSuppliers,
        note: draft.note.trim(),
        ...withAutomaticPriceMatchEstimate({
            priceMatchEstimate: draft.priceMatchEstimate,
            priceMatchCCC: hasCustomRanking ? false : draft.priceMatchCCC,
            priceMatchPartstrader: hasCustomRanking ? false : draft.priceMatchPartstrader,
            priceMatchCustom: hasCustomRanking,
        }),
        priceMatchCustomRanking: hasCustomRanking ? draft.priceMatchCustomRanking : undefined,
        orderingThreshold: draft.orderingThreshold
            ? parseFloat(draft.orderingThreshold) / 100
            : undefined,
    };
};

export interface BuildInsuranceListArgs {
    /** The saved list this save is merged into. */
    insurance: Array<InsuranceDrpSettings>;
    draft: DrpDraft;
    /** Companies in the carrier's group, the carrier itself excluded. */
    relatedCompanies: Array<InsuranceSearchResult>;
    locations: Array<{ _id: string; title: string }>;
}

/**
 * The whole insurance array as it should look after this screen saves. POST
 * /settings/update replaces the array outright, so the merge happens here.
 *
 * One document per insurer is kept — that is what the rest of the platform
 * looks a carrier up by — and grouping is carried in `parentInsurerId`: sub
 * companies riding along get a copy of the carrier's rules and point at it,
 * branched ones keep their own rules and only borrow the parent's name.
 */
export const buildInsuranceList = ({
    insurance,
    draft,
    relatedCompanies,
    locations,
}: BuildInsuranceListArgs): Array<InsuranceDrpSettings> => {
    // Merging writes no rules of its own: the carrier simply starts following
    // the group mate the user picked. Anything that was following this carrier
    // moves up to that parent too, so the whole group lands on one set of rules
    // instead of trailing off a carrier that no longer has any.
    if (draft.joinParentId) {
        const parent = insurance.find((entry) => entry.insurerId === draft.joinParentId);
        if (!parent) return insurance;
        const follow = (entry: InsuranceDrpSettings): InsuranceDrpSettings => ({
            ...entry,
            ...ruleFieldsOf(parent),
        });

        return upsert(
            insurance,
            follow({
                ...(insurance.find((entry) => entry.insurerId === draft.insurerId) ??
                    ({
                        insurerId: draft.insurerId,
                        insurerName: draft.insurerName,
                    } as InsuranceDrpSettings)),
                insurerId: draft.insurerId,
                insurerName: draft.insurerName,
            }),
        );
    }

    const primary: InsuranceDrpSettings = toInsurancePayload(
        draft,
        draft.insurerId,
        draft.insurerName,
        locations,
    );

    let next = upsert(insurance, primary);

    for (const company of relatedCompanies) {
        const mode = draft.subModes[company._id] ?? 'none';
        const saved = insurance.find((entry) => entry.insurerId === company._id);

        if (mode === 'none') {
            next = next.filter((entry) => entry.insurerId !== company._id);
            continue;
        }

        if (mode === 'elsewhere') {
            continue;
        }

        if (mode === 'same') {
            next = upsert(next, {
                ...ruleFieldsOf(primary),
                insurerId: company._id,
                insurerName: company.name,
            });
            continue;
        }

        next = upsert(next, {
            ...(saved ? ruleFieldsOf(saved) : ruleFieldsOf(primary)),
            insurerId: company._id,
            insurerName: company.name,
        });
    }

    return next;
};

/** Replace the entry for this insurer, or append it when it is new. */
const upsert = (
    insurance: Array<InsuranceDrpSettings>,
    entry: InsuranceDrpSettings,
): Array<InsuranceDrpSettings> =>
    insurance.some((candidate) => candidate.insurerId === entry.insurerId)
        ? insurance.map((candidate) => (candidate.insurerId === entry.insurerId ? entry : candidate))
        : [...insurance, entry];
